/**
 * CustomFieldDialog Component
 * Dialog za dodavanje custom polja
 */

import { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import { toast } from 'react-toastify';
import { CustomField, FieldType } from '../types';

interface CustomFieldDialogProps {
  onAddField: (field: CustomField) => void;
  onClose: () => void;
}

export function CustomFieldDialog({ onAddField, onClose }: CustomFieldDialogProps) {
  const [label, setLabel] = useState('');
  const [staticValue, setStaticValue] = useState('');
  const [fieldType, setFieldType] = useState<FieldType>('text');

  const handleAdd = () => {
    if (!label.trim()) {
      toast.error('Unesi naziv polja');
      return;
    }

    const key = `meta_data._${label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')}`;
    const id = `custom_${Date.now()}`;

    const newField: CustomField = {
      id,
      label,
      key,
      staticValue: staticValue.trim() || undefined,
      fieldType,
    };

    onAddField(newField);
    toast.success(
      `Polje "${label}" dodano (${fieldType})${newField.staticValue ? ` = "${newField.staticValue}"` : ''}`
    );
    onClose();
  };

  return (
    <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.dark' }} variant="outlined">
      <Typography variant="body2" gutterBottom>
        Novo custom polje
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Naziv polja"
          placeholder="npr. Nabavna, PDV..."
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          sx={{ minWidth: 150 }}
        />

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Tip</InputLabel>
          <Select
            value={fieldType}
            onChange={(e) => setFieldType(e.target.value as FieldType)}
            label="Tip"
          >
            <MenuItem value="text">Tekst</MenuItem>
            <MenuItem value="number">Broj</MenuItem>
            <MenuItem value="price">Cijena</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Fiksna vrijednost (opciono)"
          placeholder="npr. 17%, 24 mjeseca..."
          value={staticValue}
          onChange={(e) => setStaticValue(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
          sx={{ minWidth: 180 }}
        />

        <Button variant="contained" size="small" onClick={handleAdd}>
          Dodaj
        </Button>

        <Button size="small" onClick={onClose}>
          Odustani
        </Button>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        <strong>Tip:</strong> Tekst = kao string, Broj/Cijena = formatira "1.234,56" → 1234.56
      </Typography>
    </Paper>
  );
}
